'use client'
import { useState } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react'

interface IProps {
  children: React.ReactNode
  text?: (string | undefined)[] | undefined | string | null
  name?: string
  css?: string
}

export const SnippetComponent = ({
  text,
  children,
  css = 'border border-black rounded-lg w-64 outline-none z-10 bg-white',
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isTooltipHovered, setIsTooltipHovered] = useState(false)

  const handleTooltipMouseEnter = () => {
    setIsTooltipHovered(true)
  }

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false)
  }

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange(nextOpen) {
      setIsOpen(nextOpen)
    },
    placement: 'top',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'none',
      }),
      shift(),
    ],
  })

  // Event listeners to change the open state
  const hover = useHover(context, { move: false, delay: { open: 0, close: 150 } })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return (
    <>
      <span
        className="text-blue-700 hover:underline"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {text}
      </span>
      <FloatingPortal>
        {(isOpen || isTooltipHovered) && (
          <span
            className={css}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
          >
            {children}
          </span>
        )}
      </FloatingPortal>
    </>
  )
}

export default SnippetComponent
